import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Users, Calendar, Megaphone, Building2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PortalLayout, SectionTitle, Panel, EmptyState } from "@/components/platform/PortalLayout";

const inputCls = "w-full bg-[#0A0A0A] border border-white/10 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";

export default function OrgDashboard() {
  const [athletes, setAthletes] = useState([]);
  const [org, setOrg] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [annForm, setAnnForm] = useState({ title: "", body: "" });

  const load = useCallback(async () => {
    try {
      const [{ data: aths }, { data: orgs }, { data: sess }, { data: anns }] = await Promise.all([
        api.get("/athletes"), api.get("/organizations"), api.get("/sessions"), api.get("/announcements"),
      ]);
      setAthletes(aths); setOrg(orgs[0] || null); setSessions(sess); setAnnouncements(anns);
    } catch (e) { toast.error("Failed to load dashboard."); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const postAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await api.post("/announcements", { title: annForm.title, body: annForm.body });
      setAnnForm({ title: "", body: "" }); toast.success("Announcement posted to your organization"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (loading) {
    return <PortalLayout roleLabel="Organization Portal"><div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div></PortalLayout>;
  }

  const stats = [
    { icon: Users, label: "Athletes", value: athletes.length },
    { icon: Building2, label: "Coaches", value: org?.coach_ids?.length || 0 },
    { icon: Calendar, label: "Sessions", value: sessions.length },
  ];

  return (
    <PortalLayout roleLabel="Organization Portal">
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-2">{org?.name || "Organization"}</h1>
      <p className="text-white/40 text-sm mb-8">{org?.type || "Organization"}</p>

      <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mb-10">
        {stats.map((s) => { const Icon = s.icon; return (
          <div key={s.label} className="bg-[#0A0A0A] p-6" data-testid={`org-stat-${s.label.toLowerCase()}`}>
            <Icon size={18} className="text-white/70 mb-3" /><p className="font-mono-accent text-3xl font-bold">{s.value}</p>
            <p className="text-white/40 text-xs uppercase tracking-wide mt-1">{s.label}</p>
          </div>); })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SectionTitle>Athlete Directory</SectionTitle>
          {athletes.length ? (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="org-roster">
              {athletes.map((a) => (
                <Panel key={a.id}><p className="font-bold uppercase tracking-tight">{a.name}</p>
                  <p className="text-white/40 text-xs mt-1">{[a.sport, a.position, a.grad_year].filter(Boolean).join(" · ") || "—"}</p></Panel>
              ))}
            </div>
          ) : <EmptyState testid="org-no-athletes">No athletes assigned to your organization yet.</EmptyState>}

          <div className="mt-8">
            <SectionTitle>Team Sessions</SectionTitle>
            {sessions.length ? sessions.map((s) => (
              <Panel key={s.id} className="mb-3"><div className="flex items-center gap-2 mb-1"><Calendar size={15} className="text-white/60" /><p className="font-bold">{s.title}</p></div><p className="text-white/50 text-sm">{new Date(s.start_time).toLocaleString()}</p></Panel>
            )) : <EmptyState testid="org-no-sessions">No sessions scheduled.</EmptyState>}
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <SectionTitle>Team Announcements</SectionTitle>
            <form onSubmit={postAnnouncement} className="space-y-3 mb-4" data-testid="org-announce-form">
              <input required placeholder="Title" data-testid="org-ann-title" value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} className={inputCls} />
              <textarea required placeholder="Message" data-testid="org-ann-body" value={annForm.body} onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })} className={inputCls} rows={3} />
              <button data-testid="org-ann-submit" className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"><Megaphone size={14} /> Post</button>
            </form>
            {announcements.length ? announcements.map((a) => (
              <Panel key={a.id} className="mb-3"><p className="font-bold text-sm">{a.title}</p><p className="text-white/60 text-sm mt-1">{a.body}</p></Panel>
            )) : <EmptyState testid="org-no-announcements">No announcements.</EmptyState>}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
